import type { AxiosInstance } from 'axios';

export type ProductKind = 'PHYSICAL' | 'DIGITAL';
export type AttributeKind = 'TEXT' | 'SELECT' | 'NUMBER' | 'BOOLEAN';

export interface Brand {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  /** The logo's image URL (the column predates the name). */
  logoFileId: string | null;
  bannerFileId: string | null;
  active: boolean;
  createdAt: string;
  _count?: { products: number };
}

export interface AttributeValue {
  id: string;
  attributeId: string;
  value: string;
  position: number;
  createdAt: string;
}

export interface Attribute {
  id: string;
  name: string;
  slug: string;
  kind: AttributeKind;
  unit: string | null;
  values: AttributeValue[];
  createdAt: string;
}

export interface Color {
  id: string;
  name: string;
  hexCode: string;
  active: boolean;
  createdAt: string;
}

export interface CategoryDiscount {
  id: string;
  categoryId: string;
  label: string;
  discountBps: number;
  active: boolean;
  startsAt: string;
  endsAt: string | null;
  createdAt: string;
  category?: { name: string };
}

export interface BulkResult {
  created: number;
  skipped: number;
  errors: string[];
}

export interface JobStatus {
  id: string;
  state: string;
  progress: number;
  result?: any;
  failedReason?: string;
}

export interface ProductExportRow {
  id: string;
  title: string;
  slug: string;
  brand: string | null;
  category: string;
  productKind: ProductKind;
  status: string;
  hsnSac: string;
  gstRate: number;
  variantCount: number;
  pricePaise: number;
  mrpPaise: number;
  createdAt: string;
}

const unwrap = <T>(p: Promise<{ data: { data: T } }>) => p.then((r) => r.data.data);

export function catalogEndpoints(http: AxiosInstance) {
  return {
    // Brands
    listBrandsPublic: () => unwrap<Brand[]>(http.get('/catalog/brands')),
    listBrands: () => unwrap<Brand[]>(http.get('/admin/catalog/brands')),
    createBrand: (body: {
      name: string;
      slug: string;
      description?: string;
      logoFileId?: string;
      bannerFileId?: string;
      active?: boolean;
    }) => http.post<Brand>('/admin/catalog/brands', body).then((r) => r.data),
    updateBrand: (
      brandId: string,
      body: Partial<{
        name: string;
        slug: string;
        description: string;
        /** null removes the logo. */
        logoFileId: string | null;
        bannerFileId: string | null;
        active: boolean;
      }>,
    ) => http.patch<Brand>(`/admin/catalog/brands/${brandId}`, body).then((r) => r.data),
    deleteBrand: (brandId: string) =>
      http.delete(`/admin/catalog/brands/${brandId}`).then((r) => r.data),
    bulkImportBrands: (brands: { name: string; slug?: string; description?: string }[]) =>
      http.post<BulkResult>('/admin/catalog/brands/bulk-import', { brands }).then((r) => r.data),

    // Attributes
    listAttributesPublic: () => unwrap<Attribute[]>(http.get('/catalog/attributes')),
    listAttributes: () => unwrap<Attribute[]>(http.get('/admin/catalog/attributes')),
    createAttribute: (body: {
      name: string;
      slug: string;
      kind?: AttributeKind;
      unit?: string;
      values?: string[];
    }) => http.post<Attribute>('/admin/catalog/attributes', body).then((r) => r.data),
    updateAttribute: (
      attributeId: string,
      body: Partial<{ name: string; kind: AttributeKind; unit: string }>,
    ) =>
      http.patch<Attribute>(`/admin/catalog/attributes/${attributeId}`, body).then((r) => r.data),
    deleteAttribute: (attributeId: string) =>
      http.delete(`/admin/catalog/attributes/${attributeId}`).then((r) => r.data),
    addAttributeValue: (attributeId: string, value: string) =>
      http
        .post<Attribute>(`/admin/catalog/attributes/${attributeId}/values`, { value })
        .then((r) => r.data),
    deleteAttributeValue: (valueId: string) =>
      http.delete(`/admin/catalog/attribute-values/${valueId}`).then((r) => r.data),

    // Colors
    listColorsPublic: () => unwrap<Color[]>(http.get('/catalog/colors')),
    listColors: () => unwrap<Color[]>(http.get('/admin/catalog/colors')),
    createColor: (body: { name: string; hexCode: string; active?: boolean }) =>
      http.post<Color>('/admin/catalog/colors', body).then((r) => r.data),
    updateColor: (
      colorId: string,
      body: Partial<{ name: string; hexCode: string; active: boolean }>,
    ) => http.patch<Color>(`/admin/catalog/colors/${colorId}`, body).then((r) => r.data),
    deleteColor: (colorId: string) =>
      http.delete(`/admin/catalog/colors/${colorId}`).then((r) => r.data),

    // Category discounts
    listCategoryDiscounts: () =>
      unwrap<CategoryDiscount[]>(http.get('/admin/catalog/category-discounts')),
    createCategoryDiscount: (body: {
      categoryId: string;
      label: string;
      discountBps: number;
      active?: boolean;
      startsAt?: string;
      endsAt?: string;
    }) =>
      http.post<CategoryDiscount>('/admin/catalog/category-discounts', body).then((r) => r.data),
    updateCategoryDiscount: (
      discountId: string,
      body: Partial<{ label: string; discountBps: number; active: boolean; endsAt: string }>,
    ) =>
      http
        .patch<CategoryDiscount>(`/admin/catalog/category-discounts/${discountId}`, body)
        .then((r) => r.data),
    deleteCategoryDiscount: (discountId: string) =>
      http.delete(`/admin/catalog/category-discounts/${discountId}`).then((r) => r.data),

    // Product bulk import / export
    bulkImportProducts: (vendorId: string, products: Record<string, unknown>[]) =>
      http
        .post<BulkResult>('/admin/catalog/products/bulk-import', { vendorId, products })
        .then((r) => r.data),
    bulkImportProductsAsync: (vendorId: string, fileUrl: string) =>
      http
        .post<{ jobId: string }>('/admin/catalog/products/bulk-import/async', { vendorId, fileUrl })
        .then((r) => r.data),
    exportProducts: (params?: {
      status?: string;
      categoryId?: string;
      vendorId?: string;
      productKind?: ProductKind;
    }) => unwrap<ProductExportRow[]>(http.get('/admin/catalog/products/bulk-export', { params })),
    exportProductsAsync: (params?: {
      status?: string;
      categoryId?: string;
      vendorId?: string;
      productKind?: ProductKind;
    }) =>
      http
        .post<{ jobId: string }>('/admin/catalog/products/bulk-export/async', null, { params })
        .then((r) => r.data),
    getJobStatus: (jobId: string, type: 'import' | 'export') =>
      http.get<JobStatus>(`/admin/catalog/jobs/${jobId}`, { params: { type } }).then((r) => r.data),
  };
}

export type CatalogEndpoints = ReturnType<typeof catalogEndpoints>;
